/**
 * WHICH SURFACE an agent's screens are mounted on — and, with it, the one
 * navigation call that means the same thing on both.
 *
 * An agent has two representations and one implementation: in-frame (the main
 * app's Home detail pane) and full-screen (its own sub-app at its subdomain).
 * Almost everything an agent renders is identical either way — that is the
 * point of one implementation — but three things cannot be:
 *
 *   - VIEWPORT OWNERSHIP. Full-screen, the agent's shell owns the viewport
 *     (`h-dvh`, its own safe-area padding). In the frame it owns a pane, and
 *     `h-dvh` there overflows the pane by the height of the app header.
 *   - THE ESCAPE HATCH. Full-screen, a shell shows ReturnToMainAgent (a hard
 *     cross-origin hop back to the twin). In the frame the twin is already
 *     around it, and that button would navigate the nurse out of the app she
 *     is standing in.
 *   - WHERE THE AGENT'S PATHS LIVE. Full-screen `/runs/x` is the URL; in the
 *     frame the same screen is `/home/a/<slug>/runs/x`, because the twin owns
 *     the router (see AgentFrame).
 *
 * The first two are read as facts (useIsFramed). The third is not something a
 * screen should have to reason about at all, so it is a call instead:
 * `useAgentNavigate()` takes the agent's OWN path and puts it where that path
 * lives on this surface. An agent that used `useNavigate` directly would work
 * full-screen and silently escape the frame in the pane — which is the bug
 * this whole mechanism exists to remove.
 */
import { createContext, useCallback, useContext, type ReactNode } from 'react'
import { useNavigate, type NavigateOptions } from 'react-router-dom'

import { agentFrameBasePath } from '@/lib/routes'
import { useHomeNavStore } from '@/stores/homeNav'

// False is the honest default: a screen rendered outside any provider is a
// sub-app rendering itself, which is what every apps/<slug>/src/App.tsx does.
const FramedContext = createContext(false)

/**
 * The prefix an agent's own paths sit behind on this surface. Empty
 * full-screen (the agent IS the app), `/home/a/<slug>` in the frame.
 */
const AgentBasePathContext = createContext<string>('')

export const FramedProvider = FramedContext.Provider

export function AgentBasePathProvider({
  agentSlug,
  children,
}: {
  agentSlug: string
  children: ReactNode
}) {
  return (
    <AgentBasePathContext.Provider value={agentFrameBasePath(agentSlug)}>
      {children}
    </AgentBasePathContext.Provider>
  )
}

/** True when the caller is mounted in Home's detail pane rather than full-screen. */
export function useIsFramed(): boolean {
  return useContext(FramedContext)
}

/**
 * Navigate to one of THIS agent's screens, by the agent's own path.
 *
 * `agentNavigate('/runs/x')` goes to `/runs/x` in the sub-app and to
 * `/home/a/<slug>/runs/x` in the frame. `agentNavigate('/')` is the agent's
 * root — its face — on both.
 */
export function useAgentNavigate(): (path: string, options?: NavigateOptions) => void {
  const navigate = useNavigate()
  const base = useContext(AgentBasePathContext)
  const markSelectionTouched = useHomeNavStore((s) => s.markSelectionTouched)
  return useCallback(
    (path: string, options?: NavigateOptions) => {
      // In the frame this push is the nurse drilling in, which is what lets a
      // phone move from the list pane to the detail pane (see the store).
      // Full-screen there is no pane to drill and no base to prefix.
      if (base) markSelectionTouched()
      // The agent's root is the base itself; every other path hangs off it.
      navigate(path === '/' ? base || '/' : `${base}${path}`, options)
    },
    [navigate, base, markSelectionTouched],
  )
}
